import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { initializeFirestore } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const firebaseConfig = {
  apiKey: "AIzaSyB2vFtC8tGcHYML0eUb2PWBLi1MAxFi3OI",
  authDomain: "lctf-projects.firebaseapp.com",
  projectId: "lctf-projects",
  storageBucket: "lctf-projects.firebasestorage.app",
  messagingSenderId: "963466008988",
  appId: "1:963466008988:web:878ddd85b05867fe3c5059",
  measurementId: "G-C1QYHNNMSY"
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
/**
 * Auto long-polling helps when WebChannel/QUIC fails (e.g. ERR_QUIC_PROTOCOL_ERROR,
 * intermittent 400s on Listen) on some networks — see Firestore troubleshooting.
 */
export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
});
export const storage = getStorage(app);

export { firebaseConfig };

let _ensureAuthPromise: Promise<void> | null = null;

/**
 * Ensure a Firebase Auth session exists for Firestore rule compliance.
 * Uses auth.authStateReady() to wait for the persisted session from IndexedDB
 * before deciding. If a real user (Google/email) is already signed in,
 * this is a no-op — it never replaces their session with anonymous.
 */
export async function ensureAuth(): Promise<void> {
  if (auth.currentUser) return;
  if (_ensureAuthPromise) return _ensureAuthPromise;
  _ensureAuthPromise = (async () => {
    try {
      // Wait for Firebase to fully resolve the persisted auth state from IndexedDB
      await auth.authStateReady();
      if (auth.currentUser) {
        return;
      }
      await signInAnonymously(auth);
    } catch (err) {
      console.error('Anonymous auth failed:', err);
    } finally {
      _ensureAuthPromise = null;
    }
  })();
  return _ensureAuthPromise;
}

// ==========================================
// FIREBASE STORAGE HELPERS FOR BIDDING
// ==========================================

/**
 * Upload a bid source document (PDF, image, CSV) to Firebase Storage
 * @param bidId - The bid ID to associate the document with
 * @param file - The file to upload
 * @returns Promise with the download URL
 */
export async function uploadBidDocument(
  bidId: string,
  file: File
): Promise<string> {
  const timestamp = Date.now();
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const storageRef = ref(
    storage,
    `bids/${bidId}/source-documents/${timestamp}-${sanitizedFileName}`
  );
  
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

/**
 * Upload a bid proposal PDF to Firebase Storage
 * @param bidId - The bid ID
 * @param version - The version number of the proposal
 * @param pdfBlob - The PDF blob to upload
 * @returns Promise with the download URL
 */
export async function uploadBidProposal(
  bidId: string,
  bidNumber: string,
  version: number,
  pdfBlob: Blob
): Promise<string> {
  const storageRef = ref(
    storage,
    `bids/${bidId}/proposals/${bidNumber}-v${version}.pdf`
  );
  
  await uploadBytes(storageRef, pdfBlob);
  return getDownloadURL(storageRef);
}

/**
 * Delete a file from Firebase Storage
 * @param url - The download URL of the file to delete
 */
export async function deleteBidFile(url: string): Promise<void> {
  try {
    // Extract the path from the URL
    const urlObj = new URL(url);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+?)(\?|$)/);
    if (pathMatch && pathMatch[1]) {
      const path = decodeURIComponent(pathMatch[1]);
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    }
  } catch (error) {
    console.error('Error deleting file from storage:', error);
    throw error;
  }
}

/**
 * Delete all files associated with a bid
 * @param bidId - The bid ID
 */
export async function deleteBidFiles(bidId: string): Promise<void> {
  // Note: Firebase Storage doesn't support recursive deletion from client-side
  // This would typically be handled by a Cloud Function or backend service
  // For now, we'll delete individual files as needed
  console.warn('Bulk file deletion should be handled by Cloud Functions');
}
